import React, { ReactNode } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { Loader2 } from 'lucide-react';
import { DuckLoadingScreen } from '@/components/DuckLoadingScreen';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface TenancyGuardProps {
  children: ReactNode;
  loadingComponent?: ReactNode;
  errorComponent?: (error: Error) => ReactNode;
}

/**
 * A component that ensures tenancy is loaded before rendering its children.
 * Shows a loading state while tenancy is being loaded and an error state if loading fails.
 */
export const TenancyGuard: React.FC<TenancyGuardProps> = ({
  children,
  loadingComponent,
  errorComponent,
}) => {
  const { loading, activeTenancy } = useTenancy();

  // Default error component
  const defaultError = (error: Error) => (
    <div className="container mx-auto p-4">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error loading tenancy</AlertTitle>
        <AlertDescription>
          {error.message || 'Failed to load tenancy data. Please try refreshing the page.'}
        </AlertDescription>
      </Alert>
    </div>
  );

  // Handle different states without early returns
  let content;

  if (loading) {
    content = <>{loadingComponent || <DuckLoadingScreen />}</>;
  } else if (activeTenancy && activeTenancy.id) {
    content = <>{children}</>;
  } else {
    // If we don't have a tenancy, show no tenancy message
    content = (
      <div className="container mx-auto p-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No tenancy selected</AlertTitle>
          <AlertDescription>
            Please select a tenancy to continue.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return content;
};

/**
 * Higher-order component for class components
 */
export function withTenancyGuard<T>(
  WrappedComponent: React.ComponentType<T>,
  options: Omit<TenancyGuardProps, 'children'> = {}
) {
  return (props: T) => (
    <TenancyGuard {...options}>
      <WrappedComponent {...(props as T)} />
    </TenancyGuard>
  );
}
